import { useState, useEffect, useCallback } from 'react';
import { getAmplifyClient } from '../../../lib/amplifyClient';

export interface UseGetAvailableYearsReturn {
  yearsWithData: number[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook to fetch which years have imported hourly data
 * Checks current year and 2 previous years
 */
export function useGetAvailableYears(): UseGetAvailableYearsReturn {
  const [yearsWithData, setYearsWithData] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAvailableYears = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const currentYear = new Date().getFullYear();
      const yearsToCheck = [currentYear - 2, currentYear - 1, currentYear];
      const client = getAmplifyClient();

      // Fetch import summary for each year
      const results = await Promise.all(
        yearsToCheck.map(async (year) => {
          try {
            const inputJson = JSON.stringify({ year });
            const result = await client.queries.getImportSummary({
              input: inputJson,
            });

            if (result.errors && result.errors.length > 0) {
              return { year, hasData: false };
            }

            if (!result.data) {
              return { year, hasData: false };
            }

            // Parse the AWSJSON response
            let parsedResult: any;

            if (typeof result.data === 'string') {
              try {
                parsedResult = JSON.parse(result.data);

                // Handle double-stringified response
                if (typeof parsedResult === 'string') {
                  parsedResult = JSON.parse(parsedResult);
                }
              } catch {
                return { year, hasData: false };
              }
            } else if (typeof result.data === 'object' && result.data !== null) {
              parsedResult = result.data;
            } else {
              return { year, hasData: false };
            }

            // Check if year has data (totalMonths > 0)
            const hasData = parsedResult.totalMonths > 0;

            return { year, hasData };
          } catch {
            // If query fails, assume no data for this year
            return { year, hasData: false };
          }
        })
      );

      // Extract years that have data
      const yearsWithImports = results
        .filter((r) => r.hasData)
        .map((r) => r.year)
        .sort((a, b) => a - b);

      setYearsWithData(yearsWithImports);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Błąd pobierania dostępnych lat';
      setError(message);
      setYearsWithData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchAvailableYears();
  }, [fetchAvailableYears]);

  return {
    yearsWithData,
    loading,
    error,
    refetch: fetchAvailableYears,
  };
}
